import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '@/shared/api/client'
import { endpoints } from '@/shared/api/endpoints'
import { useAppStore } from '@/shared/stores/appStore'
import type {
  GwHistoryEntry,
  MySquadResult,
  PlayerListItem,
  PreviewCopySquadResult,
  SaveSquadPayload,
  UserTeamResult,
} from './types'

// ── Query key factories ───────────────────────────────────────────────────────

export const squadKeys = {
  all:     ['squads'] as const,
  mySquad: (leagueId: string, gameweekId?: string) =>
    gameweekId
      ? [...squadKeys.all, 'my-squad', leagueId, gameweekId] as const
      : [...squadKeys.all, 'my-squad', leagueId] as const,
  userTeam: (leagueId: string, userId: string, gameweekId?: string) =>
    gameweekId
      ? [...squadKeys.all, 'user-team', leagueId, userId, gameweekId] as const
      : [...squadKeys.all, 'user-team', leagueId, userId] as const,
}

export type ServerFilter = {
  search:        string
  position:      'ALL' | 'GK' | 'DEF' | 'MID' | 'FWD'
  sortBy:        'price' | 'points' | 'lastGw' | 'avgPoints' | 'name'
  sortDir:       'asc' | 'desc'
  competitionId: string
  teamCode:      string                // '' = all teams
  minPrice:      number | null         // null = no lower bound
  maxPrice:      number | null         // null = no upper bound
  hideEliminated: boolean              // true = exclude players on eliminated teams (server-side)
}

export const playerKeys = {
  all:    ['players'] as const,
  list:   (filter: ServerFilter) => [...playerKeys.all, 'list', filter] as const,
  detail: (id: string)           => [...playerKeys.all, id]             as const,
}

export const competitionTeamKeys = {
  all:  ['competition-teams'] as const,
  list: (competitionId: string) => [...competitionTeamKeys.all, competitionId] as const,
}

// ── Competition teams ─────────────────────────────────────────────────────────

export type CompetitionTeam = {
  id:           string
  code:         string
  name:         string
  teamType:     'NATIONAL' | 'CLUB'
  logoUrl:      string | null
  isEliminated: boolean
  groupCode:    string | null
}

export async function fetchCompetitionTeams(competitionId: string): Promise<CompetitionTeam[]> {
  return apiClient.get<CompetitionTeam[]>(endpoints.competitions.teams(competitionId))
}

export function useCompetitionTeams(competitionId: string) {
  return useQuery({
    queryKey: competitionTeamKeys.list(competitionId),
    queryFn:  () => fetchCompetitionTeams(competitionId),
    enabled:  !!competitionId,
    staleTime: 10 * 60_000,
  })
}

// ── My squad ──────────────────────────────────────────────────────────────────

export async function fetchMySquad(leagueId: string, gameweekId?: string): Promise<MySquadResult> {
  const url = gameweekId
    ? `${endpoints.squads.my(leagueId)}?gameweekId=${encodeURIComponent(gameweekId)}`
    : endpoints.squads.my(leagueId)
  return apiClient.get<MySquadResult>(url)
}

export function useMySquad(leagueId: string, gameweekId?: string) {
  return useQuery({
    queryKey: squadKeys.mySquad(leagueId, gameweekId),
    queryFn:  () => fetchMySquad(leagueId, gameweekId),
    staleTime: gameweekId ? Infinity : 60_000, // historical snapshots never go stale
  })
}

// ── Another user's squad (visibility-gated) ───────────────────────────────────

export async function fetchUserTeam(
  leagueId:   string,
  userId:     string,
  gameweekId?: string,
): Promise<UserTeamResult> {
  return apiClient.get<UserTeamResult>(endpoints.squads.userTeam(leagueId, userId, gameweekId))
}

export function useUserTeam(leagueId: string, userId: string, gameweekId?: string) {
  return useQuery({
    queryKey: squadKeys.userTeam(leagueId, userId, gameweekId),
    queryFn:  () => fetchUserTeam(leagueId, userId, gameweekId),
    staleTime: gameweekId ? Infinity : 60_000,
    retry: (failureCount, error) => {
      // 403 squad_not_visible is a steady-state response, not a transient error
      if (error instanceof ApiError && error.status === 403) return false
      return failureCount < 1
    },
  })
}

// ── Player list ───────────────────────────────────────────────────────────────

// Raw shape as returned by GET /v1/players — field is `id`, not `playerId`
type PlayerApiGwHistoryDto = {
  gameweekNumber:        number
  points:                number
  opponent:              string | null
  result:                string | null
  played:                boolean
  minutes:               number
  goals:                 number
  assists:               number
  cleanSheet:            boolean
  defensiveContribution: number
  saves:                 number
  goalsConceded:         number
  ownGoals:              number
  yellowCards:           number
  redCards:              number
  penaltiesSaved:        number
  successDribbles:       number
  penaltiesMissed:       number
  penaltiesWon:          number
  shotsOnTarget:         number
}

type PlayerApiDto = {
  id:            string
  name:          string
  displayName:   string
  position:      string
  price:         number
  isAvailable:   boolean
  isEliminated:  boolean
  teamCode:      string
  teamName:      string
  teamLogoUrl?:  string | null
  teamId?:       string
  groupCode:     string | null
  injuryStatus?: string
  totalPoints?:  number
  gwPoints?:     number | null
  avgPoints?:    number | null
  nextOpponent?: string | null
  gwHistory?:    PlayerApiGwHistoryDto[]
}

const PLAYERS_PAGE_SIZE = 100
export const PLAYERS_PAGE_SIZE_PAGINATED = 8

type PlayersApiResponse = {
  items:    PlayerApiDto[]
  hasMore:  boolean
  total:    number
  pageSize: number
  priceMin: number
  priceMax: number
}

export type PlayersPage = {
  items:    PlayerListItem[]
  hasMore:  boolean
  total:    number
  priceMin: number
  priceMax: number
}

function mapPlayerDto(dto: PlayerApiDto): PlayerListItem {
  return {
    playerId:     dto.id,
    name:         dto.name,
    displayName:  dto.displayName,
    position:     dto.position as PlayerListItem['position'],
    price:        dto.price,
    isAvailable:  dto.isAvailable,
    isEliminated: dto.isEliminated,
    teamCode:     dto.teamCode,
    teamName:     dto.teamName,
    teamLogoUrl:  dto.teamLogoUrl ?? null,
    teamId:       dto.teamId ?? '',
    injuryStatus: (dto.injuryStatus as PlayerListItem['injuryStatus'] | undefined) ?? 'NONE',
    totalPoints:  dto.totalPoints ?? 0,
    gwPoints:     dto.gwPoints ?? null,
    avgPoints:    dto.avgPoints ?? null,
    nextOpponent: dto.nextOpponent ?? null,
    gwHistory:    (dto.gwHistory ?? []).map<GwHistoryEntry>(h => ({
      gameweekNumber:        h.gameweekNumber,
      points:                h.points,
      opponent:              h.opponent,
      result:                h.result,
      played:                h.played,
      minutes:               h.minutes,
      goals:                 h.goals,
      assists:               h.assists,
      cleanSheet:            h.cleanSheet,
      defensiveContribution: h.defensiveContribution,
      saves:                 h.saves,
      goalsConceded:         h.goalsConceded,
      ownGoals:              h.ownGoals,
      yellowCards:           h.yellowCards,
      redCards:              h.redCards,
      penaltiesSaved:        h.penaltiesSaved,
      successDribbles:       h.successDribbles,
      penaltiesMissed:       h.penaltiesMissed,
      penaltiesWon:          h.penaltiesWon,
      shotsOnTarget:         h.shotsOnTarget,
    })),
  }
}

async function fetchPlayersPage(filter: ServerFilter, skip: number, pageSize = PLAYERS_PAGE_SIZE): Promise<PlayersPage> {
  const params = new URLSearchParams({
    pageSize: String(pageSize),
    skip:     String(skip),
    sortBy:   filter.sortBy.toLowerCase(), // camelCase → lowercase for the API
    sortDir:  filter.sortDir,
  })
  if (filter.search.trim())        params.set('search',        filter.search.trim())
  if (filter.position !== 'ALL')   params.set('position',      filter.position)
  if (filter.competitionId)        params.set('competitionId', filter.competitionId)
  if (filter.teamCode)             params.set('teamCode',      filter.teamCode)
  if (filter.minPrice !== null)    params.set('minPrice',      String(filter.minPrice))
  if (filter.maxPrice !== null)    params.set('maxPrice',      String(filter.maxPrice))
  if (filter.hideEliminated)       params.set('excludeEliminated', 'true')

  const res = await apiClient.get<PlayersApiResponse>(`${endpoints.players.list()}?${params.toString()}`)
  return {
    items:    res.items.map(mapPlayerDto),
    hasMore:  res.hasMore,
    total:    res.total,
    priceMin: res.priceMin,
    priceMax: res.priceMax,
  }
}

export function usePlayerList(filter: ServerFilter) {
  return useInfiniteQuery({
    queryKey:         playerKeys.list(filter),
    queryFn:          ({ pageParam }) => fetchPlayersPage(filter, pageParam),
    initialPageParam: 0 as number,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.flatMap(p => p.items).length : undefined,
    // Don't fire until the league's competitionId is known — otherwise the API
    // returns players from every competition and the user sees the wrong pool.
    enabled:   !!filter.competitionId,
    staleTime: 5 * 60_000,
    gcTime:    10 * 60_000,
  })
}

export function usePaginatedPlayerList(filter: ServerFilter, skip: number) {
  return useQuery({
    queryKey:        [...playerKeys.list(filter), 'page', skip] as const,
    queryFn:         () => fetchPlayersPage(filter, skip, PLAYERS_PAGE_SIZE_PAGINATED),
    enabled:         !!filter.competitionId,
    staleTime:       5 * 60_000,
    placeholderData: keepPreviousData,
  })
}

export function usePlayerById(playerId: string | null) {
  return useQuery({
    queryKey:  playerKeys.detail(playerId ?? ''),
    queryFn:   async () => {
      const dto = await apiClient.get<PlayerApiDto>(endpoints.players.detail(playerId!))
      return mapPlayerDto(dto)
    },
    enabled:   playerId !== null,
    staleTime: 5 * 60_000,
  })
}

// ── Copy squad preview ────────────────────────────────────────────────────────
//
// User-triggered (mutation, not subscription). The modal flow passes the user-picked
// source league ID — this hook resolves the source squadId via the cached my-squad
// query, then calls /v1/squads/{targetSquadId}/copy-preview?sourceSquadId=...

type CopyPreviewVariables = {
  targetSquadId:  string
  sourceLeagueId: string
}

export function useCopyPreviewMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ targetSquadId, sourceLeagueId }: CopyPreviewVariables) => {
      const sourceSquad = await queryClient.ensureQueryData({
        queryKey:  squadKeys.mySquad(sourceLeagueId),
        queryFn:   () => fetchMySquad(sourceLeagueId),
        staleTime: 60_000,
      })
      return apiClient.get<PreviewCopySquadResult>(
        endpoints.squads.copyPreview(targetSquadId, sourceSquad.squadId),
      )
    },
  })
}

// ── Save squad ────────────────────────────────────────────────────────────────

type SaveSquadVariables = {
  squadId: string
  payload: SaveSquadPayload
}

const SAVE_ERROR_MESSAGES: Partial<Record<string, string>> = {
  squad_locked:           'A janela de transferências está fechada.',
  transfer_window_closed: 'A janela de transferências está fechada.',
  invalid_formation:      'Formação inválida. Verifique os jogadores titulares.',
  player_eliminated:      'Um jogador da sua escalação foi eliminado da Copa.',
  player_not_available:   'Um jogador da sua escalação não está mais disponível. Remova-o para salvar.',
  duplicate_player:       'Há jogadores repetidos na escalação.',
  budget_exceeded:        'Orçamento excedido. Remova jogadores mais caros.',
}

// Save errors whose backend `detail` names the specific player(s) involved — surfaced verbatim.
const DETAIL_PREFERRED_SAVE_CODES = new Set(['player_not_available', 'player_eliminated'])

export function useSaveSquad(leagueId: string) {
  const queryClient = useQueryClient()
  const addToast    = useAppStore(s => s.addToast)

  return useMutation({
    mutationFn: ({ squadId, payload }: SaveSquadVariables) =>
      apiClient.put<void>(endpoints.squads.save(squadId), payload),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: squadKeys.mySquad(leagueId) })
      addToast({ type: 'success', title: 'Escalação salva!' })
    },

    onError: (error: unknown) => {
      const code   = error instanceof ApiError ? error.errorCode : 'unknown_error'
      const detail = error instanceof ApiError ? error.detail : undefined
      // For these codes the backend `detail` names the exact player(s) to remove — show it
      // verbatim so the user knows who to drop. Other codes keep a curated generic (detail can be
      // a bare HTTP status text when the backend sends none, so we don't surface it indiscriminately).
      const message = (detail && DETAIL_PREFERRED_SAVE_CODES.has(code))
        ? detail
        : SAVE_ERROR_MESSAGES[code] ?? 'Erro ao salvar escalação. Tente novamente.'
      addToast({ type: 'error', title: 'Erro ao salvar', body: message })
    },
  })
}
